/**
 * Authentication helper functions
 */

/**
 * Sets the authentication data in local storage
 * @param {string} token The JWT token
 * @param {object} user The user object
 */
export const setAuthData = (token, user) => {
  localStorage.setItem("authToken", token);
  localStorage.setItem("user", JSON.stringify(user));
  
  // Store token timestamp for expiration tracking
  localStorage.setItem("tokenTimestamp", Date.now().toString());
};

/**
 * Retrieves the authentication token from local storage
 * @returns {string|null} The authentication token or null if not found
 */
export const getAuthToken = () => {
  return localStorage.getItem("authToken");
};

/**
 * Retrieves the user data from local storage
 * @returns {object|null} The user object or null if not found
 */
export const getUser = () => {
  const userData = localStorage.getItem("user");
  return userData ? JSON.parse(userData) : null;
};

/**
 * Decodes a JWT token to extract payload information
 * @param {string} token The JWT token
 * @returns {object|null} The decoded payload or null if invalid
 */
export const decodeToken = (token) => {
  try {
    if (!token) return null;
    
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const payload = parts[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return decoded;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Checks if a token is expired
 * @param {string} token The JWT token
 * @returns {boolean} True if token is expired, false otherwise
 */
export const isTokenExpired = (token) => {
  if (!token) return true;
  
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    // If we can't decode the token or there's no expiration, 
    // fall back to timestamp-based check (4 hours)
    const tokenTimestamp = localStorage.getItem("tokenTimestamp");
    if (!tokenTimestamp) return true;
    
    const fourHours = 4 * 60 * 60 * 1000; // 4 hours in milliseconds
    return (Date.now() - parseInt(tokenTimestamp)) > fourHours;
  }
  
  // Check if token is expired (exp is in seconds, Date.now() is in milliseconds)
  return decoded.exp * 1000 < Date.now();
};

/**
 * Checks if the user is authenticated and token is valid
 * @returns {boolean} True if the user is authenticated with valid token, false otherwise
 */
export const isAuthenticated = () => {
  const token = getAuthToken();
  if (!token) return false;
  
  if (isTokenExpired(token)) {
    // Token is expired, clear auth data
    clearAuthData();
    return false;
  }
  
  return true;
};

/**
 * Forces user logout by clearing all auth data and redirecting to login
 */
export const forceLogout = () => {
  clearAuthData();
  
  // Show a user-friendly message
  if (window.location.pathname !== '/login') {
    console.log('Session expired. Redirecting to login...');
    
    // Optional: Show a toast notification if available
    if (window.toast) {
      window.toast.error('Your session has expired. Please log in again.');
    }
    
    // Redirect to login page
    window.location.href = '/login';
  }
};

/**
 * Clears the authentication data from local storage
 */
export const clearAuthData = () => {
  localStorage.removeItem("authToken");
  localStorage.removeItem("user");
  localStorage.removeItem("tokenTimestamp");
};